import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Tooltip } from "antd";
import { ArrowRightOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import useAxios from "../../custom-hooks/useAxios";
import { useProperty } from "../../custom-hooks/useProperties";
import { EMPTY_ASSIGNEES, useWorkers } from "../../custom-hooks/useWorkers";
import { InlineErrorMessage, InlineLoader } from "../../components/Loader";
import { formatTranslation } from "../../utils/translationUtils";
import { YieldAnalysisStatus } from "../../utils/yieldAnalysis";
import { useTrackingEvent } from "../../custom-hooks/useTracking";
import { NavigationEvents } from "../../utils/amplitudeEvents";
import ProtcolDrawer from "../ProtocolDrawer";
import SummaryRatesAndDosage from "../../../pages/new-trail/SummaryComponent/ratesAndDosage";
import {
  NewTrialStateProvider,
  useNewTrialState,
} from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import { mergeStripPlotsIntoGroup } from "../../../pages/new-trail/hooks/splitStripPlots";
import {
  buildFieldNames,
  buildPlotDesign,
  buildStripDesign,
  buildStripsData,
  formatPreviewDate,
  getStatusTranslationKey,
  hasOwnProtocolId,
  isStripTrial,
  resolveLinkedProtocolId,
} from "./trialPreviewMapper";
import {
  InfoCell,
  LinkedProtocolSection,
  PlotDesignSection,
  ProtocolViewTitle,
  ReadinessSection,
  Section,
  StripDesignSection,
} from "./sections";
import {
  useMergedTrial,
  useProtocolDetail,
  useProtocolIdByName,
  useRequestHeaders,
  useTrialDetail,
  useYieldReadiness,
  type PreviewRequestHeaders,
} from "./useTrialPreview";
import "./TrialPreviewDrawer.less";

interface TrialPreviewDrawerProps {
  trialId: string;
  open: boolean;
  onClose: () => void;
  /**
   * Row data the preview was opened from. Painted immediately so the drawer has
   * content while `/trial/{id}` is in flight, then merged with the response —
   * the row carries fields the detail endpoint does not return (e.g. `crop`).
   */
  fallbackTrial?: any;
  orgId?: string;
  propertyId?: string;
  /**
   * Notified when the linked protocol is opened, with its id and an optional
   * name (best-effort: may be undefined if not yet loaded). The protocol is
   * shown inside this drawer either way — a `renderInline` host uses this to
   * keep its own title/back-button in sync, since it owns the chrome around
   * this drawer's content in that mode.
   */
  onViewProtocol?: (protocolId: string, protocolName?: string) => void;
  /** Navigates to the full trial edit/detail page. */
  onOpenTrial?: (trialId: string) => void;
  extraHeaders?: PreviewRequestHeaders;
  /**
   * Renders just the trial content, without the surrounding Drawer. Set by a
   * host (e.g. the protocol drawer's Linked Trials table) that shows the
   * preview inside the sidebar it already owns instead of opening a drawer of
   * its own.
   */
  renderInline?: boolean;
  /**
   * Bumped by a `renderInline` host to force this drawer back from its nested
   * protocol view to the trial view — e.g. when the host's own back button is
   * used to leave the protocol view it surfaced via `onViewProtocol`. Ignored
   * otherwise; this drawer manages `showProtocol` itself in every other case.
   */
  collapseProtocolViewSignal?: number;
}

/**
 * Feeds the strip geometry into the freshly mounted NewTrialStateProvider. The
 * Rates & Dosage grid reads per-strip areas from `newTrialState.stripsData` —
 * during trial creation the map writes it, and without it here every strip row
 * falls back to the first strip's area, so all quantities come out identical.
 */
const StripsStateBridge: React.FC<{ stripsData: any }> = ({ stripsData }) => {
  const { setNewTrialState } = useNewTrialState();

  useEffect(() => {
    if (!stripsData) return;
    setNewTrialState((previous: any) => ({ ...previous, stripsData }));
  }, [stripsData, setNewTrialState]);

  return null;
};

/**
 * Right-side preview of a single trial, opened from the Trials table. Mirrors
 * the Protocol preview drawer and follows the CWTRC-2060 design: trial header,
 * linked protocol, plot design, rates & dosage, status, data readiness and
 * navigation shortcuts.
 *
 * The linked protocol takes over this same sidebar rather than opening a second
 * drawer on top: only the body and the title change, so the panel stays put.
 */
const TrialPreviewDrawer: React.FC<TrialPreviewDrawerProps> = ({
  trialId,
  open,
  onClose,
  fallbackTrial,
  orgId,
  propertyId,
  onViewProtocol,
  onOpenTrial,
  extraHeaders,
  renderInline = false,
  collapseProtocolViewSignal,
}) => {
  const { t } = useTranslation();
  const headers = useRequestHeaders(extraHeaders);

  // Which view owns the sidebar: the trial preview, or its linked protocol.
  const [showProtocol, setShowProtocol] = useState(false);

  useEffect(() => {
    if (renderInline && collapseProtocolViewSignal !== undefined) {
      setShowProtocol(false);
    }
  }, [renderInline, collapseProtocolViewSignal]);

  const { trialData, isLoading, hasError } = useTrialDetail(
    trialId,
    open,
    headers
  );
  const trial = useMergedTrial(trialData, fallbackTrial);

  const resolvedOrgId: string | undefined = trial?.org_id ?? orgId;
  const resolvedPropertyId: string | undefined =
    trial?.property_id ?? propertyId;

  const protocolIdByName = useProtocolIdByName(
    open,
    trial?.protocol_name ?? "",
    !hasOwnProtocolId(trial),
    resolvedOrgId,
    headers
  );
  const linkedProtocolId = resolveLinkedProtocolId(trial, protocolIdByName);
  const protocolData = useProtocolDetail(open, linkedProtocolId, headers);
  const trackNavigationEvents = useTrackingEvent({
    page: "Trials Page",
    group: "Trials",
    module: "Trials",
    trial_id: trialId,
    protocol_id: linkedProtocolId ?? undefined,
    crop_type: trial?.crop_type ?? trial?.crop ?? protocolData?.crop,
  });

  const readiness = useYieldReadiness({
    open,
    fieldIds: trial?.field_ids,
    trialId,
    endDate: trial?.end_date,
  });

  // A freshly opened sidebar always starts on the trial, never on the protocol
  // view a previous visit left behind.
  useEffect(() => {
    if (open) setShowProtocol(false);
  }, [open, trialId]);

  const property = useProperty(resolvedPropertyId);

  /**
   * The trial payload carries `assignee_id`, not a usable `assignee_name`, so
   * the owner's display name is resolved from the property's worker list —
   * the same lookup the trial Summary step uses.
   */
  const { data: assignees = EMPTY_ASSIGNEES } = useWorkers(
    resolvedOrgId,
    resolvedPropertyId
  );
  const ownerName =
    assignees.find((assignee) => assignee.value === trial?.assignee_id)
      ?.label ?? trial?.assignee_name;

  // Field ids only become names once the property's fields are known.
  const { response: farmProperties } = useAxios(
    resolvedPropertyId
      ? `/properties/${String(resolvedPropertyId)}/fields?attributes=geometry`
      : "",
    true
  );
  const fieldNames = useMemo(
    () => buildFieldNames(farmProperties?.content, trial?.field_ids),
    [farmProperties, trial?.field_ids]
  );

  /**
   * `/trial/{id}` returns a strip trial as one `trial_plots` entry per strip.
   * The Summary step folds those into a single "Strips Group" before rendering
   * — without the same fold, Plot Design and Rates & Dosage list one group per
   * strip instead of one group holding every strip as a row.
   */
  const groupedTrialPlots = useMemo(
    () =>
      mergeStripPlotsIntoGroup(
        trial?.trial_plots,
        trial?.strip_design?.is_strip === true
      ),
    [trial?.trial_plots, trial?.strip_design?.is_strip]
  );

  const plotDesign = useMemo(
    () => buildPlotDesign({ ...trial, trial_plots: groupedTrialPlots }),
    [trial, groupedTrialPlots]
  );

  /**
   * A strip trial gets a Strip Design section instead of Plot Design, the same
   * split the trial Summary step makes. Both are built from the unmerged payload
   * so every strip is listed, not just the group they fold into.
   */
  const isStrips = isStripTrial(trial);
  const stripDesign = useMemo(() => buildStripDesign(trial), [trial]);
  const stripsData = useMemo(() => buildStripsData(trial), [trial]);

  /*
   * The service returns the Explorer deep link ready to use (EXP008 §6), so a
   * route change on their side needs no release here. An empty value means
   * there are no results to link to.
   */
  const explorerUrl = readiness.explorerUrl;
  const isExplorerEnabled =
    readiness.status === YieldAnalysisStatus.Available && Boolean(explorerUrl);

  const explorerLink = (
    <button
      type="button"
      className="preview-link"
      data-testid="view-in-explorer-btn"
      disabled={!isExplorerEnabled}
      onClick={() => {
        void trackNavigationEvents({
          event: NavigationEvents.YIELD_ANALYSIS,
          trial_id: trialId,
          protocol_id: linkedProtocolId ?? undefined,
          crop_type: trial?.crop_type ?? trial?.crop ?? protocolData?.crop,
        });
        window.open(explorerUrl, "_blank", "noopener,noreferrer");
      }}
    >
      {formatTranslation(t("trialPreview.viewResultsInExplorer"))}
      <ArrowRightOutlined />
    </button>
  );

  const trialTitle = trial?.name ?? formatTranslation(t("trialPreview.title"));

  const content = (
    <>
      {showProtocol && (
        <ProtcolDrawer
          protocolId={linkedProtocolId}
          open
          onClose={onClose}
          extraHeaders={extraHeaders}
          renderInline
          showLinkedTrials
        />
      )}

      {!showProtocol && isLoading && !trial && <InlineLoader padding />}

      {!showProtocol && !(isLoading && !trial) && (
        <div className="trial-preview-container" data-testid="trial-preview">
          {hasError && !trialData && (
            <InlineErrorMessage
              text={formatTranslation(t("trialPreview.loadError"))}
            />
          )}

          <div className="trial-basic-info">
            <InfoCell label={formatTranslation(t("trials.tableTitle4"))}>
              {fieldNames.length > 0 ? fieldNames.join(", ") : undefined}
            </InfoCell>
            <InfoCell label={formatTranslation(t("trialPreview.farm"))}>
              {property?.data?.name}
            </InfoCell>
            <InfoCell
              label={formatTranslation(
                t("createProtocol.basicInfo.protocolCrop")
              )}
            >
              {/*
                `/trial/{id}` does not return `crop`: it comes from the list row
                the preview was opened from, or from the linked protocol when
                the drawer is opened without one.
              */}
              {trial?.crop ?? protocolData?.crop}
            </InfoCell>
            <InfoCell label={formatTranslation(t("trialPreview.owner"))}>
              {ownerName}
            </InfoCell>
          </div>

          <LinkedProtocolSection
            protocolName={trial?.protocol_name}
            canView={Boolean(linkedProtocolId)}
            onView={() => {
              setShowProtocol(true);
              onViewProtocol?.(
                linkedProtocolId,
                protocolData?.name ?? trial?.protocol_name
              );
            }}
          />

          {/*
            The design section is always present, even before any plot or strip
            has been laid out — a trial with none says so rather than dropping
            the section and leaving the reader guessing.
          */}
          {isStrips ? (
            <StripDesignSection rows={stripDesign} />
          ) : (
            <PlotDesignSection entries={plotDesign} />
          )}

          {/*
            Rates & Dosage reuses the trial Summary step's component verbatim so
            the preview shows the same seeds / CP & biologicals / nutrients
            breakdown, with nothing omitted. It reads UI state (active tab,
            biological application index) from NewTrialStateProvider, which is
            mounted here because the Trials list sits outside the trial-creation
            flow that normally provides it.
          */}
          {trialData && protocolData && (
            <div className="trial-preview-section rates-dosage-section">
              <h3>{formatTranslation(t("newTrial.ratesDosage.title"))}</h3>
              <NewTrialStateProvider>
                <StripsStateBridge stripsData={stripsData} />
                <SummaryRatesAndDosage
                  data={{ ...trialData, trial_plots: groupedTrialPlots }}
                  trialProtocolData={protocolData}
                  hideProtocolInfo
                  showTabIcons
                />
              </NewTrialStateProvider>
            </div>
          )}

          <Section title={formatTranslation(t("trialPreview.trialStatus"))}>
            <div className="trial-basic-info">
              <InfoCell label={formatTranslation(t("trials.status"))}>
                <span className="status-tag">
                  {formatTranslation(t(getStatusTranslationKey(trial)))}
                </span>
              </InfoCell>
              <InfoCell label={formatTranslation(t("trials.tableTitle5"))}>
                {formatPreviewDate(trial?.start_date)}
              </InfoCell>
              <InfoCell
                label={formatTranslation(t("trialPreview.nextActivity"))}
              >
                {formatPreviewDate(trial?.next_activity_date)}
              </InfoCell>
              <InfoCell label={formatTranslation(t("trials.tableTitle6"))}>
                {formatPreviewDate(trial?.end_date)}
              </InfoCell>
            </div>
          </Section>

          <ReadinessSection status={readiness.status} />

          <Section title={formatTranslation(t("trialPreview.navigateTo"))}>
            <div className="navigate-links">
              <button
                type="button"
                className="preview-link"
                data-testid="open-trial-btn"
                onClick={() => {
                  onOpenTrial?.(trialId);
                }}
              >
                {formatTranslation(t("trialPreview.openTrial"))}
                <ArrowRightOutlined />
              </button>

              {isExplorerEnabled ? (
                explorerLink
              ) : (
                <Tooltip
                  title={formatTranslation(
                    t("trialPreview.yieldNotAvailableTooltip")
                  )}
                >
                  {/* Wrapper keeps the tooltip reachable while the link is disabled. */}
                  <span className="disabled-link-wrapper">{explorerLink}</span>
                </Tooltip>
              )}
            </div>
          </Section>
        </div>
      )}
    </>
  );

  if (renderInline) {
    if (!open) return null;
    return <div className="trial-preview-inline">{content}</div>;
  }

  return (
    <Drawer
      title={
        showProtocol ? (
          <ProtocolViewTitle
            name={protocolData?.name ?? trial?.protocol_name}
            onBack={() => {
              setShowProtocol(false);
            }}
          />
        ) : (
          trialTitle
        )
      }
      placement="right"
      width={481}
      open={open}
      onClose={onClose}
      /*
       * antd renders its own close button *before* the title, which put the X to
       * the left of the back arrow. Supplying it as `extra` instead places it
       * after the title in the DOM, so the header reads back / name / close
       * without depending on CSS ordering.
       */
      closable={false}
      extra={
        <button
          type="button"
          className="preview-close-btn"
          data-testid="close-preview-btn"
          aria-label={formatTranslation(t("trialPreview.close"))}
          onClick={onClose}
        >
          <CloseOutlined />
        </button>
      }
      rootClassName="trial-preview-drawer"
    >
      {content}
    </Drawer>
  );
};

export default TrialPreviewDrawer;
